import React, { useState } from "react";
import "./About.css";

interface TeamMember {
  id: string;
  name: string;
  title: string;
  subtitle?: string;
  image: string;
  link?: string;
  badge?: string;
}

const About: React.FC = () => {
  const [isCiteModalOpen, setIsCiteModalOpen] = useState(false);

  const teamMembers: TeamMember[] = [
    {
      id: "jlm",
      name: "José Montano",
      title: "Team Lead",
      subtitle: "NSF Graduate Research Fellow",
      image: "/authors/jlm.jpg",
    },
    {
      id: "vx",
      name: "Vee Xu",
      title: "Research Engineer",
      subtitle: "Gladstone Institutes",
      image: "/authors/vx.png",
    },
    {
      id: "vrt",
      name: "Vishnu Rajan Tejus",
      title: "Research Intern",
      subtitle: "Zaro Lab",
      image: "/authors/vrt.jpg",
    },
    {
      id: "zaro",
      name: "Balyn W. Zaro, Ph.D.",
      title: "Associate Professor",
      subtitle: "Pharmaceutical Chemistry",
      image: "/authors/zaro.jpg",
      link: "https://pharm.ucsf.edu/zaro",
    },
  ];

  return (
    <div className="about-page">
      {/* Paper Section */}
      <section className="paper-section">
        <div className="paper-card">
          <div className="paper-thumbnail-wrapper">
            <img
              src="https://paper-assets.alphaxiv.org/image/2507.07955v1.png"
              alt="Paper thumbnail"
              className="paper-thumbnail"
            />
            <div className="paper-stats">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stats-icon"
              >
                <line x1="18" x2="18" y1="20" y2="10"></line>
                <line x1="12" x2="12" y1="20" y2="4"></line>
                <line x1="6" x2="6" y1="20" y2="14"></line>
              </svg>
              <span>2,157</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="trending-icon"
              >
                <path d="M16 7h6v6"></path>
                <path d="m22 7-8.5 8.5-5-5L2 17"></path>
              </svg>
            </div>
          </div>

          <div className="paper-content-wrapper">
            <div className="paper-meta">
              <span className="paper-date">Dec 2025</span>
              <div className="paper-tags">
                <span className="paper-tag">cysteine-profiling</span>
                <span className="paper-tag">proteomics</span>
                <span className="paper-tag">drug-discovery</span>
              </div>
            </div>

            <a
              href="https://www.cell.com/cell-chemical-biology/pdf/S2451-9456(25)00396-4.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="paper-title-link"
            >
              <h2 className="paper-title">
                An Interactive Resource Mapping the Proteome and Reactive
                Cysteine Landscape Across the NCI-60 Reveals Cell and
                Tissue-Specific Profiles
              </h2>
            </a>

            <div className="paper-affiliations">
              <span className="affiliation-chip">
                <img
                  src="/ucsf-logo.png"
                  alt="UCSF"
                  className="affiliation-logo"
                />
                <span>UC San Francisco</span>
              </span>
            </div>

            <div className="paper-summary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="sparkle-icon"
              >
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
                <path d="M20 3v4"></path>
                <path d="M22 5h-4"></path>
                <path d="M4 17v2"></path>
                <path d="M5 18H3"></path>
              </svg>
              <span>
                The NCI-60 cancer cell line panel is one of the most extensively characterized and publicly accessible resources in cancer research. Here, we expand this platform by generating a comprehensive proteomic and cysteine reactivity atlas using shotgun proteomics and quantitative chemoproteomics. We quantified over 12,000 proteins and identified more than 36,000 reactive cysteines, including over 10,000 hyperreactive sites, across the panel. Our analyses reveal widespread heterogeneity in cysteine reactivity, while conserved hyperreactive cysteines strongly correlate with functional roles. Tissue-specific cysteine reactivity occurred independently of protein abundance, highlighting context-dependent regulation. To enable exploration and integration with existing datasets, we developed an interactive online database that allows users to visualize protein and cysteine coverage and design custom cell line panels. Together, these data and tools illuminate the landscape of cysteine reactivity in cancer and provide a foundational resource to advance future studies in protein function, redox biology, and covalent drug discovery.
              </span>
            </div>

            <div className="paper-authors">
              <p className="authors-text">
                José L Montaño<sup>1</sup>, Vishnu R Tejus<sup>2</sup>, Vee Xu
                <sup>3</sup>, Andrew C Condon<sup>1</sup>, Andrew K Ecker
                <sup>1</sup>, Andreas Langen<sup>1</sup>, Kevin K Leung<sup>2</sup>, Balyn W Zaro
                <sup>4</sup>
              </p>
              <p className="authors-note">
                <sup>1</sup>Department of Pharmaceutical Chemistry, University
                of California San Francisco, San Francisco, CA
                <br />
                <sup>2</sup>Cardiovascular Research Institute, University of
                California San Francisco, San Francisco, CA
                <br />
                <sup>3</sup>Gladstone Institutes, San Francisco, CA
                <br />
                <sup>4</sup>Department of Pharmaceutical Chemistry and Cardiovascular Research Institute, University of California San Francisco, San Francisco, CA
                <br /><br />
                <strong>Cell Chemical Biology</strong> • PMID: 41478280 • DOI:{" "}
                <a
                  href="https://doi.org/10.1016/j.chembiol.2025.12.003"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#0071e3", textDecoration: "none" }}
                  onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                  10.1016/j.chembiol.2025.12.003
                </a>
              </p>
            </div>

            <div className="paper-actions">
              <button
                className="bookmark-button"
                onClick={() => setIsCiteModalOpen(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path>
                </svg>
                <span>Cite this paper</span>
              </button>
              <a
                href="https://www.cell.com/cell-chemical-biology/pdf/S2451-9456(25)00396-4.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="bookmark-button"
                style={{ textDecoration: "none" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <span>Read the paper</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Citation Modal */}
      {isCiteModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsCiteModalOpen(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cite this paper</h2>
              <button
                className="modal-close"
                onClick={() => setIsCiteModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="citation-section">
                <h3>MLA</h3>
                <p className="citation-text">
                  Montaño, José L., et al. "An interactive resource mapping the
                  proteome and reactive cysteine landscape across the NCI-60
                  reveals cell and tissue-specific profiles." <em>Cell Chemical
                  Biology</em> (2025).
                </p>
              </div>

              <div className="citation-section">
                <h3>APA</h3>
                <p className="citation-text">
                  Montaño, J. L., Tejus, V. R., Xu, V., Condon, A. C., Ecker,
                  A. K., Langen, A., ... & Zaro, B. W. (2025). An interactive
                  resource mapping the proteome and reactive cysteine landscape
                  across the NCI-60 reveals cell and tissue-specific profiles.
                  <em> Cell Chemical Biology</em>.
                </p>
              </div>

              <div className="citation-section">
                <h3>Chicago</h3>
                <p className="citation-text">
                  Montaño, José L., Vishnu R. Tejus, Vee Xu, Andrew C. Condon,
                  Andrew K. Ecker, Andreas Langen, Kevin K. Leung, and Balyn W.
                  Zaro. "An interactive resource mapping the proteome and
                  reactive cysteine landscape across the NCI-60 reveals cell and
                  tissue-specific profiles." <em>Cell Chemical Biology</em> (2025).
                </p>
              </div>

              <div className="citation-section">
                <h3>Harvard</h3>
                <p className="citation-text">
                  Montaño, J.L., Tejus, V.R., Xu, V., Condon, A.C., Ecker,
                  A.K., Langen, A., Leung, K.K. and Zaro, B.W., 2025. An
                  interactive resource mapping the proteome and reactive
                  cysteine landscape across the NCI-60 reveals cell and
                  tissue-specific profiles. <em>Cell Chemical Biology</em>.
                </p>
              </div>

              <div className="citation-section">
                <h3>Vancouver</h3>
                <p className="citation-text">
                  Montaño JL, Tejus VR, Xu V, Condon AC, Ecker AK, Langen A,
                  Leung KK, Zaro BW. An interactive resource mapping the
                  proteome and reactive cysteine landscape across the NCI-60
                  reveals cell and tissue-specific profiles. Cell Chemical
                  Biology. 2025 Dec 31.
                </p>
              </div>

              <div className="citation-section">
                <h3>BibTeX</h3>
                <pre className="citation-text bibtex">
{`@article{montano2025interactive,
  title={An interactive resource mapping the proteome and reactive cysteine landscape across the NCI-60 reveals cell and tissue-specific profiles},
  author={Monta{\\~n}o, Jos{\\'e} L and Tejus, Vishnu R and Xu, Vee and Condon, Andrew C and Ecker, Andrew K and Langen, Andreas and Leung, Kevin K and Zaro, Balyn W},
  journal={Cell Chemical Biology},
  publisher={Elsevier}
}`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Section */}
      <section className="section-content relative">
        <h1 className="typography-subsection-headline">Meet the CysTeam</h1>

        <div className="team-section-wrapper">
          <div className="relative team-molecule-container">
            <svg
              className="team-molecule"
              version="1.0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 800 523"
              preserveAspectRatio="xMidYMid meet"
            >
              <g
                transform="translate(0.000000,523.000000) scale(0.100000,-0.100000)"
                fill="#000000"
                stroke="none"
              >
                <path d="M5295 5091 c-56 -14 -110 -46 -153 -89 -110 -109 -131 -312 -50 -465 36 -68 72 -100 148 -136 46 -22 69 -26 140 -26 93 1 163 25 227 79 45 38 93 141 103 218 28 239 -103 415 -314 424 -39 2 -84 0 -101 -5z m153 -83 c72 -18 134 -85 157 -171 35 -129 5 -261 -76 -330 -116 -100 -286 -63 -362 78 -19 34 -22 56 -22 150 1 94 4 117 24 157 25 51 71 92 129 113 47 17 90 18 150 3z"/>
                <path d="M5220 3686 l0 -594 -407 -235 c-225 -129 -505 -291 -624 -359 l-216 -125 -694 400 c-382 221 -703 401 -714 401 -20 1 -1075 -603 -1075 -615 0 -4 8 -17 19 -30 l18 -23 506 292 c279 161 513 295 521 298 8 3 311 -166 695 -387 670 -386 681 -393 681 -423 0 -17 -25 -274 -55 -571 -30 -297 -55 -552 -55 -567 l0 -28 155 0 156 0 -6 33 c-8 50 -116 1159 -113 1162 14 11 1363 785 1369 785 4 0 232 -130 508 -290 276 -159 505 -290 510 -290 4 0 13 12 20 27 10 23 10 29 -2 38 -8 6 -209 122 -446 259 l-431 248 0 594 0 594 -35 0 -35 0 0 -570 c0 -313 -3 -570 -6 -570 -4 0 -24 10 -45 22 l-39 22 -39 -22 c-21 -12 -41 -22 -45 -22 -3 0 -6 257 -6 570 l0 570 -35 0 -35 0 0 -594z"/>
                <path d="M1060 2651 c-72 -22 -119 -62 -145 -125 -42 -99 21 -197 150 -234 39 -11 103 -30 144 -42 92 -27 117 -43 131 -85 27 -82 -45 -144 -168 -145 -103 0 -185 48 -198 117 -8 41 -15 46 -59 37 -30 -5 -35 -10 -35 -33 1 -37 32 -101 66 -133 54 -50 119 -72 220 -72 82 -1 96 2 149 29 78 40 118 100 118 179 0 111 -56 159 -246 209 -71 19 -141 43 -155 54 -57 42 -52 100 11 147 23 17 43 21 105 21 111 0 148 -25 175 -115 4 -12 15 -15 46 -12 36 4 41 7 41 28 0 35 -34 97 -70 127 -56 48 -200 72 -280 48z"/>
                <path d="M6673 2641 c-126 -43 -203 -149 -219 -303 -17 -165 61 -315 195 -376 70 -32 180 -36 259 -9 70 25 153 101 181 167 70 163 27 381 -93 471 -90 68 -215 88 -323 50z m192 -72 c64 -18 128 -91 150 -172 22 -82 17 -173 -14 -242 -42 -96 -107 -138 -211 -139 -97 -1 -170 45 -216 134 -22 45 -31 175 -15 245 31 138 163 213 306 174z"/>
                <path d="M200 2295 l0 -355 45 0 45 0 2 168 3 167 180 0 180 0 3 -167 2 -168 45 0 45 0 0 355 0 355 -45 0 -45 0 0 -145 0 -145 -185 0 -185 0 0 145 0 145 -45 0 -45 0 0 -355z"/>
                <path d="M7250 2295 l0 -355 45 0 45 0 2 168 3 167 180 0 180 0 3 -167 2 -168 45 0 45 0 0 355 0 355 -45 0 -45 0 0 -145 0 -145 -185 0 -185 0 0 145 0 145 -45 0 -45 0 0 -355z"/>
                <path d="M3690 670 l0 -350 45 0 45 0 2 273 3 272 183 -272 182 -273 50 0 50 0 0 350 0 350 -45 0 -45 0 -2 -272 -3 -273 -183 273 -182 272 -50 0 -50 0 0 -350z"/>
                <path d="M4410 670 l0 -350 45 0 45 0 0 165 0 165 185 0 185 0 0 -165 0 -165 45 0 45 0 0 350 0 350 -45 0 -44 0 -3 -142 -3 -143 -182 -3 -183 -2 0 145 0 145 -45 0 -45 0 0 -350z"/>
                <path d="M5145 641 c-42 -26 -61 -50 -73 -93 -10 -36 -10 -36 24 -40 23 -3 34 0 34 9 0 26 25 65 51 79 67 34 149 -5 149 -72 0 -54 -20 -83 -135 -189 -98 -91 -145 -152 -145 -190 0 -13 24 -15 175 -15 l175 0 0 35 0 35 -125 0 c-100 0 -123 3 -119 13 3 8 45 49 95 91 107 92 140 139 146 204 5 56 -19 101 -69 132 -44 27 -141 27 -183 1z"/>
              </g>
            </svg>

            {/* Team member floaties positioned on letters */}
            {/* HS (left) - José */}
            <div className="team-floatie" style={{ top: "45%", left: "15%" }}>
              <img
                src={teamMembers[0].image}
                alt={teamMembers[0].name}
                className="floatie-image"
              />
              <div className="floatie-tooltip">
                <div className="tooltip-name">{teamMembers[0].name}</div>
                <div className="tooltip-title">{teamMembers[0].title}</div>
              </div>
            </div>

            {/* O (top center) - Vee */}
            <div className="team-floatie" style={{ top: "5%", left: "63%" }}>
              <img
                src={teamMembers[1].image}
                alt={teamMembers[1].name}
                className="floatie-image"
              />
              <div className="floatie-tooltip">
                <div className="tooltip-name">{teamMembers[1].name}</div>
                <div className="tooltip-title">{teamMembers[1].title}</div>
              </div>
            </div>

            {/* NH2 (bottom) - Vishnu */}
            <div className="team-floatie" style={{ top: "85%", left: "50%" }}>
              <img
                src={teamMembers[2].image}
                alt={teamMembers[2].name}
                className="floatie-image"
              />
              <div className="floatie-tooltip">
                <div className="tooltip-name">{teamMembers[2].name}</div>
                <div className="tooltip-title">{teamMembers[2].title}</div>
              </div>
            </div>

            {/* OH (right) - Zaro */}
            <div className="team-floatie" style={{ top: "45%", left: "88%" }}>
              <img
                src={teamMembers[3].image}
                alt={teamMembers[3].name}
                className="floatie-image"
              />
              <div className="floatie-tooltip">
                <div className="tooltip-name">{teamMembers[3].name}</div>
                <div className="tooltip-title">{teamMembers[3].title}</div>
              </div>
            </div>
          </div>

          {/* Win Image - Desktop Only */}
          <div className="win-section">
            <img
              src="/authors/win.png"
              alt="First Prize Winner"
              className="win-badge"
            />
            <div className="win-text">
              <h3 className="win-title">First Prize Winner</h3>
              <p className="win-subtitle">UCSF-QBI Hackathon</p>
            </div>
          </div>
        </div>
      </section>

      {/* Achievement Section */}
      {/* <section className="achievement-section">
        <div className="achievement-content">
          <img
            src="/authors/win.png"
            alt="First Prize Winner"
            className="win-image"
          />
          <div className="achievement-text">
            <h2 className="achievement-title">First Prize Winner</h2>
            <p className="achievement-subtitle">UCSF-QBI Hackathon</p>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default About;